// VerifyOtp — shared step for the register, login, and password-reset flows.
// The `otpType` in router state decides where a verified code leads and
// where the back button returns. Calls users/verify-otp.

import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Icon, OtpInput } from '@/components/common';
import AuthLayout from '@/layout/AuthLayout';
import { OTPType, useSendOtpMutation, useVerifyOtpMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import { setAuth } from '@/redux/auth/authSlice';
import { useAppDispatch } from '@/store/hooks';
import useOtpCooldown from '@/hooks/useOtpCooldown';
import { phoneOtpKey } from '@/redux/otp/otpKeys';
import AuthBack from './AuthBack';

const OTP_LENGTH = 4;

interface OtpState {
  otpType: OTPType;
  dialCode?: string;
  phone?: string;
}

const VerifyOtp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const state = (location.state ?? {}) as OtpState;

  const [verifyOtp, { isLoading: submitting }] = useVerifyOtpMutation();
  const [sendOtp, { isLoading: resending }] = useSendOtpMutation();

  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | undefined>();

  const otpType = state.otpType ?? OTPType.RESET_PASSWORD;
  const target = state.phone ? `${state.dialCode ?? ''} ${state.phone}`.trim() : '';
  // The send that led here already started this cooldown, so the resend link
  // mounts disabled and counts down. No phone (direct visit) → inert.
  const cooldown = useOtpCooldown(
    state.phone ? phoneOtpKey(otpType, state.dialCode ?? '', state.phone) : null,
  );
  const BACK_BY_TYPE: Record<OTPType, string> = {
    [OTPType.REGISTER]: '/signup',
    [OTPType.LOGIN]: '/login',
    [OTPType.RESET_PASSWORD]: '/forgot-password',
  };
  const backTo = BACK_BY_TYPE[otpType] ?? '/login';

  const setValue = (value: string) => {
    setOtp(value);
    if (error) setError(undefined);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.length < OTP_LENGTH) {
      setError('error_invalid_otp');
      return;
    }

    try {
      const result = await verifyOtp({
        countryCode: state.dialCode ?? '',
        mobileNumber: state.phone ?? '',
        otpType,
        code: otp,
      }).unwrap();

      // The verify response carries a single-use token that authorizes the
      // subsequent reset-password call (attached as its Authorization header).
      if (result?.validationKey) {
        dispatch(setAuth({ validationKey: result.validationKey }));
      }

      if (otpType === OTPType.RESET_PASSWORD) {
        navigate('/reset-password', { state });
        return;
      } else if (otpType === OTPType.LOGIN) {
        if (result?.hasPassword === false) {
          // Account verified but has no login password yet — set one first.
          toast(t('toast_password_not_set'));
          navigate('/set-password', {
            state: { otpType, dialCode: state.dialCode, phone: state.phone },
          });
          return;
        }
      }

      // OTP verified (register, or login with an existing password). verify-otp
      // returns no tokens, so send the user to login to sign in.
      toast.success(t('toast_account_verified'));
      navigate('/login');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  const handleResend = async () => {
    // The disabled button is the UI contract; this covers keyboard activation
    // and any state landing mid-flight.
    if (cooldown.active || resending) return;
    try {
      await sendOtp({
        countryCode: state.dialCode ?? '',
        mobileNumber: state.phone ?? '',
        otpType,
      }).unwrap();
      toast.success(t('toast_otp_sent'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <AuthBack onClick={() => navigate(backTo)} />
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_otp_verification')}</h1>
          <p className='auth-page__subtitle'>{t('text_otp_sent_to')}</p>
          {target && (
            <p className='auth-page__phone' dir='ltr'>
              {target}
            </p>
          )}
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <div className='auth-page__otp'>
            <OtpInput
              length={OTP_LENGTH}
              value={otp}
              onChange={setValue}
              error={!!error}
              autoFocus
            />
            {error && (
              <span className='app-field__error' role='alert'>
                <Icon name='circle-alert' size={15} aria-hidden='true' />
                {t(error)}
              </span>
            )}
          </div>

          <Button type='submit' size='lg' loading={submitting} className='auth-page__submit'>
            {t('button_verify')}
          </Button>

          <p className='auth-page__alt'>
            {t('text_didnt_receive_otp')}{' '}
            <button
              type='button'
              className='auth-page__link'
              onClick={handleResend}
              disabled={cooldown.active || resending}
            >
              {cooldown.active
                ? t('text_resend_otp_in', { time: cooldown.label })
                : t('text_resend_otp')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default VerifyOtp;
